import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";
import { formatDurationShort } from "@/utils/time";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

export const useDwellTime = (
  storeId: number[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawSeconds, setRawSeconds] = useState<number | null>(null);
  const beginRequest = useLatestRequestGuard();

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!storeId || !dateRange) {
      setRawSeconds(null);
      return;
    }

    const fetchDwellTime = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const params: any = {
          storeIds: storeId,
          startDate: storePerformanceService.formatDate(dateRange.start, browserTimezone),
          endDate: storePerformanceService.formatDate(dateRange.end, browserTimezone),
        };
        if (deviceType && deviceType !== "all") params.deviceType = deviceType;
        if (areaIdsKey) params.areaIds = areaIdsKey.split(",").map(Number);
        const response = await analyticsService.getSessionAnalytics(params);

        const totalSessions =
          deviceType === "IN_AISLE"
            ? (response?.data?.totalSessionsIncludingNoItems ?? 0)
            : (response?.data?.totalSessions ?? 0);
        const totalDwellMs =
          deviceType === "IN_AISLE"
            ? (response?.data?.totalDwellMillisecondIncludingNoItems ?? 0)
            : (response?.data?.totalDwellMillisecond ?? 0);

        if (totalSessions <= 0 || totalDwellMs <= 0) {
          if (isCurrent()) setRawSeconds(null);
          return;
        }

        const avgMs = totalDwellMs / totalSessions;
        if (isCurrent()) setRawSeconds(avgMs / 1000);
      } catch (error) {
        console.error("Error fetching dwell time:", error);
        if (isCurrent()) setRawSeconds(null);
      }
    };

    fetchDwellTime();
  }, [
    Array.isArray(storeId) ? storeId.join(",") : (storeId ?? null),
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    deviceType,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (rawSeconds == null) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: null,
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    return {
      previousValue: null,
      value: formatDurationShort(rawSeconds, currentLanguage),
      subtitle: i18n.t("common.analytics.averageTimeInFittingRoom"),
      isPositive: true,
      pillText: null,
      rawValue: rawSeconds,
    };
  }, [rawSeconds, currentLanguage]);
};
